import {
  FilterAction,
  FilterActions,
  FilterKeys,
  VehicleFilter,
  VehicleMileageFilterState,
} from "@modules/vehicle/types/vehicleFilter";
import Slider from "@components/form/Slider";
import FilterDisclosure from "@modules/vehicle/components/FilterDisclosure";

import { ChangeEvent, useEffect, useState } from "react";
import Input from "@components/form/Input";
import { useTranslation } from "next-i18next";
import { useNumberMask } from "@common/hooks/useMask";

export interface VehicleMileageFilterProps {
  defaultMin: number;
  defaultMax: number;
  onUpdate(filterAction: FilterAction): void;
  vehicleFilters: VehicleFilter[];
  locale?: string;
}

const VehicleMileageFilter = ({
  defaultMin,
  defaultMax,
  onUpdate,
  vehicleFilters,
  locale = "en",
}: VehicleMileageFilterProps) => {
  const { t } = useTranslation(["common"]);
  const inputDelayMs = 2000;

  const mileageFilter = vehicleFilters.find(
    (f) => f.key === FilterKeys.Mileage
  );
  const mileageFilterState = mileageFilter
    ? (mileageFilter.state as VehicleMileageFilterState)
    : null;

  const userModified = mileageFilter !== undefined;

  const [minInputValue, setMinInputValue] = useState<number>(
    mileageFilterState !== null ? mileageFilterState.minMileage : defaultMin
  );
  const [maxInputValue, setMaxInputValue] = useState<number>(
    mileageFilterState !== null ? mileageFilterState.maxMileage : defaultMax
  );

  const [minMileageTimer, setMinMileageTimer] = useState<NodeJS.Timer>();
  const [maxMileageTimer, setMaxMileageTimer] = useState<NodeJS.Timer>();
  const { maskPipe: maskNumberPipe, unMaskPipe: unMaskNumberPipe } =
    useNumberMask();

  const updateSharedState = (
    minMileageValue: number,
    maxMileageValue: number
  ) => {
    const isDefault =
      minMileageValue === defaultMin && maxMileageValue === defaultMax;

    if (isDefault) {
      onUpdate({
        type: FilterActions.RemoveFilter,
        payload: FilterKeys.Mileage,
      });
    } else {
      onUpdate({
        type: FilterActions.AddMileageFilter,
        payload: { minMileage: minMileageValue, maxMileage: maxMileageValue },
      });
    }
  };

  useEffect(() => {
    if (mileageFilterState !== null) {
      setMinInputValue(mileageFilterState.minMileage);
      setMaxInputValue(mileageFilterState.maxMileage);
    } else {
      setMinInputValue(defaultMin);
      setMaxInputValue(defaultMax);
    }
  }, [defaultMin, defaultMax, mileageFilterState]);

  const onChangeMinMileage = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const strippedValue = unMaskNumberPipe(value);

    if (!Number.isNaN(strippedValue)) {
      setMinInputValue(strippedValue);
      clearTimeout(minMileageTimer);

      const newTimer = setTimeout(() => {
        if (strippedValue <= maxInputValue && strippedValue >= defaultMin) {
          updateSharedState(strippedValue, maxInputValue);
        } else {
          setMinInputValue(mileageFilterState?.minMileage ?? defaultMin);
        }
      }, inputDelayMs);

      setMinMileageTimer(newTimer);
    }
  };

  const onChangeMaxMileage = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const strippedValue = unMaskNumberPipe(value);

    if (!Number.isNaN(strippedValue)) {
      setMaxInputValue(strippedValue);
      clearTimeout(maxMileageTimer);

      const newTimer = setTimeout(() => {
        if (strippedValue >= minInputValue && strippedValue <= defaultMax) {
          updateSharedState(minInputValue, strippedValue);
        } else {
          setMaxInputValue(mileageFilterState?.maxMileage ?? defaultMax);
        }
      }, inputDelayMs);

      setMaxMileageTimer(newTimer);
    }
  };

  const onAfterSliderChange = (
    sliderChangeValue: number | readonly number[]
  ) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minMileageValue = sliderChangeValueArray[0];
    const maxMileageValue = sliderChangeValueArray[1];

    if (minMileageValue !== undefined && maxMileageValue !== undefined) {
      updateSharedState(minMileageValue, maxMileageValue);
    }
  };

  const onSliderChange = (sliderChangeValue: number | readonly number[]) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minMileageValue = sliderChangeValueArray[0];
    const maxMileageValue = sliderChangeValueArray[1];

    if (minMileageValue !== undefined && maxMileageValue !== undefined) {
      setMinInputValue(minMileageValue);
      setMaxInputValue(maxMileageValue);
    }
  };

  return (
    <FilterDisclosure active={userModified} headerText={t("common:mileage")}>
      <div>
        <div className="vehicle-mileage-filter">
          <div className="flex gap-4">
            <div
              className={`w-full ${
                minInputValue > defaultMin ? "text-primary-bold" : ""
              }`}
            >
              <Input
                id="minimumMileageFilter"
                label={t("common:min").toUpperCase()}
                onChange={onChangeMinMileage}
                value={maskNumberPipe(minInputValue.toString())}
              />
            </div>

            <div
              className={`w-full ${
                maxInputValue < defaultMax ? "text-primary-bold" : ""
              }`}
            >
              <Input
                id="maximumMileageFilter"
                label={t("common:max").toUpperCase()}
                onChange={onChangeMaxMileage}
                value={maskNumberPipe(maxInputValue.toString())}
              />
            </div>
          </div>

          <div className="pt-2">
            <Slider
              className="horizontal-slider"
              min={defaultMin}
              max={defaultMax}
              value={[minInputValue, maxInputValue]}
              defaultValue={[defaultMin, defaultMax]}
              onAfterChange={onAfterSliderChange}
              onChange={onSliderChange}
              step={1000}
              formatDisplayValue="locale"
              locale={locale}
              ariaLabel="Vehicle Mileage Slider"
            />
          </div>
        </div>
        <div />
      </div>
    </FilterDisclosure>
  );
};
export default VehicleMileageFilter;
